/**
 * AdminOrderCreatePage — manual order entry by admin.
 *
 * Use cases: phone orders, in-store pickups, free samples, admin-paid
 * orders. Lets the admin pick a customer (existing or inline-create),
 * add line items, choose the initial status (created/paid/delivered),
 * and submit. The server runs allocateFifo for paid/delivered orders.
 *
 * The form runs entirely client-side: customers are searched live via
 * users.list({search}); products are pre-loaded on first focus via
 * products.list({limit:100}). Totals update as items are added.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, UserPlus, Search } from 'lucide-react'
import { toast } from 'sonner'

import {
  orders,
  products,
  users,
  type AdminProduct,
  type AdminUserListItem,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { FormField } from '../components/FormField'
import { Input, Select, Textarea } from '../components/Input'
import { formatINR } from '../lib/format'

interface DraftItem {
  productId: string
  productName: string
  price: number // paise
  quantity: number
}

/** Initial statuses the admin can pick for a new order. Distinct
 *  from the full OrderStatus union because the create endpoint
 *  rejects 'cancelled' and 'returned' (those require an existing
 *  order to flip). */
type AdminCreateStatus = 'created' | 'paid' | 'delivered'

const SHIPPING_FREE_THRESHOLD_PAISE = 99900 // ₹999
const SHIPPING_FEE_PAISE = 5000 // ₹50
const GST_RATE = 0.05

export function AdminOrderCreatePage() {
  const navigate = useNavigate()

  // Customer state
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerResults, setCustomerResults] = useState<AdminUserListItem[]>([])
  const [customerLoading, setCustomerLoading] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<AdminUserListItem | null>(null)
  const [creatingNew, setCreatingNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newEmail, setNewEmail] = useState('')

  // Line items
  const [items, setItems] = useState<DraftItem[]>([])
  const [productList, setProductList] = useState<AdminProduct[]>([])
  const [productsLoaded, setProductsLoaded] = useState(false)
  const [addProductId, setAddProductId] = useState('')
  const [addQty, setAddQty] = useState('1')

  // Address
  const [addrName, setAddrName] = useState('')
  const [addrPhone, setAddrPhone] = useState('')
  const [addrLine1, setAddrLine1] = useState('')
  const [addrLine2, setAddrLine2] = useState('')
  const [addrCity, setAddrCity] = useState('')
  const [addrState, setAddrState] = useState('')
  const [addrPincode, setAddrPincode] = useState('')

  // Order meta
  const [status, setStatus] = useState<AdminCreateStatus>('paid')
  const [adminNote, setAdminNote] = useState('')

  // Submission
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Search customers on debounce. typing triggers the search; we use
  // a small delay so we don't fire on every keystroke.
  useEffect(() => {
    if (selectedCustomer) return
    if (!customerSearch.trim() || customerSearch.trim().length < 3) {
      setCustomerResults([])
      return
    }
    const handle = setTimeout(() => {
      setCustomerLoading(true)
      users
        .list({ search: customerSearch.trim(), limit: 10 })
        .then((r) => setCustomerResults(r.users))
        .catch((err) =>
          toast.error('Customer search failed', { description: err.message }),
        )
        .finally(() => setCustomerLoading(false))
    }, 250)
    return () => clearTimeout(handle)
  }, [customerSearch, selectedCustomer])

  // Pre-load the product list once when the user first opens the
  // products dropdown. The fetch is lazy to keep initial page load
  // snappy.
  function ensureProductListLoaded() {
    if (productsLoaded) return
    products
      .list({ limit: 100 })
      .then((r) => {
        setProductList(r.products.filter((p) => p.isActive))
        setProductsLoaded(true)
      })
      .catch((err) =>
        toast.error('Failed to load products', { description: err.message }),
      )
  }

  // Auto-fill address fields when an existing customer is picked.
  // We use the customer's name + phone; the rest of the address
  // the admin still has to enter.
  function pickCustomer(c: AdminUserListItem) {
    setSelectedCustomer(c)
    setCustomerSearch('')
    setCustomerResults([])
    setCreatingNew(false)
    setAddrName(c.name)
    setAddrPhone(c.phone)
  }

  function clearCustomer() {
    setSelectedCustomer(null)
    setAddrName('')
    setAddrPhone('')
  }

  function startCreateNew() {
    setSelectedCustomer(null)
    setCreatingNew(true)
    setAddrName(newName)
    setAddrPhone(newPhone)
  }

  function addItem() {
    const product = productList.find((p) => p.id === addProductId)
    if (!product) return
    const qty = parseInt(addQty, 10)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be a positive number')
      return
    }
    // Refuse up front if the picked quantity exceeds current stock.
    // For 'paid'/'delivered' orders the server also runs allocateFifo
    // which would 409 anyway, but catching it here gives the admin a
    // clearer message before they bother saving.
    if (qty > product.totalStock) {
      toast.error(
        `Only ${product.totalStock} of "${product.name}" available in stock`,
      )
      return
    }
    setItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity: qty,
      },
    ])
    setAddProductId('')
    setAddQty('1')
  }

  function removeItem(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }

  // Totals — re-derive whenever items change. Matches the seed's
  // pattern of treating prices as paise throughout.
  const subtotalPaise = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items],
  )
  const gstPaise = useMemo(
    () => Math.round(subtotalPaise * GST_RATE),
    [subtotalPaise],
  )
  const shippingPaise = useMemo(
    () => (subtotalPaise >= SHIPPING_FREE_THRESHOLD_PAISE ? 0 : SHIPPING_FEE_PAISE),
    [subtotalPaise],
  )
  const totalPaise = subtotalPaise + gstPaise + shippingPaise

  async function handleSubmit() {
    setError(null)
    if (!selectedCustomer && !creatingNew) {
      setError('Pick a customer or create a new one')
      return
    }
    if (items.length === 0) {
      setError('Add at least one line item')
      return
    }
    if (addrName.trim().length < 2 || addrPhone.trim().length < 8 || addrLine1.trim().length < 3) {
      setError('Name, phone, and address line 1 are required')
      return
    }
    if (addrPincode.trim().length !== 6) {
      setError('Pincode must be 6 digits')
      return
    }

    setSubmitting(true)
    try {
      const result = await orders.adminCreate({
        customer: selectedCustomer
          ? { id: selectedCustomer.id }
          : {
              phone: newPhone.trim(),
              name: newName.trim(),
              email: newEmail.trim() || null,
            },
        items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
        shippingAddress: {
          full_name: addrName.trim(),
          phone: addrPhone.trim(),
          line1: addrLine1.trim(),
          line2: addrLine2.trim() || undefined,
          city: addrCity.trim(),
          state: addrState.trim(),
          pincode: addrPincode.trim(),
          country: 'India',
        },
        status: status,
        adminNote: adminNote.trim() || undefined,
      })
      toast.success(`Order ${result.order.orderNumber} created`)
      navigate(`/orders/${result.order.id}`)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create order')
      toast.error('Create failed', { description: err?.message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-5 max-w-5xl">
      <Link
        to="/orders"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to orders
      </Link>

      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-ink">New order</h1>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-danger flex items-start gap-2">
          <span className="font-medium">Could not create order:</span> {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
        {/* Left column: customer, items, address, note */}
        <div className="space-y-5 min-w-0">
          {/* Customer picker */}
          <Card className="p-5 space-y-3">
            <h2 className="font-display text-base font-semibold text-ink">Customer</h2>
            {selectedCustomer ? (
              <div className="flex items-center justify-between rounded-md border border-line bg-ivory p-3">
                <div>
                  <p className="text-sm font-medium text-ink">{selectedCustomer.name}</p>
                  <p className="text-xs text-slatey">{selectedCustomer.phone}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearCustomer}
                >
                  Change
                </Button>
              </div>
            ) : creatingNew ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Full name" required>
                    <Input
                      value={newName}
                      onChange={(e) => {
                        setNewName(e.target.value)
                        setAddrName(e.target.value)
                      }}
                      required
                    />
                  </FormField>
                  <FormField label="Phone" required>
                    <Input
                      value={newPhone}
                      onChange={(e) => {
                        setNewPhone(e.target.value)
                        setAddrPhone(e.target.value)
                      }}
                      placeholder="10-digit phone"
                      required
                    />
                  </FormField>
                  <FormField label="Email (optional)">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </FormField>
                </div>
                <p className="text-xs text-slatey">
                  The server will create this customer on submit. Phone must
                  be unique.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
                  <Input
                    placeholder="Search by name or phone (min 3 chars)"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onFocus={() => setCreatingNew(false)}
                    className="pl-9"
                  />
                </div>
                {customerLoading ? (
                  <p className="text-xs text-slatey">Searching…</p>
                ) : customerResults.length > 0 ? (
                  <ul className="rounded-md border border-line divide-y divide-line max-h-64 overflow-y-auto">
                    {customerResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => pickCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-ivory"
                        >
                          <p className="text-sm text-ink">{c.name}</p>
                          <p className="text-xs text-slatey">
                            {c.phone}
                            {c.email ? ` · ${c.email}` : ''}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : customerSearch.length >= 3 ? (
                  <p className="text-xs text-slatey">No matches. Create a new customer below.</p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={startCreateNew}
                >
                  <UserPlus className="size-3.5" /> Create new customer
                </Button>
              </div>
            )}
          </Card>

          {/* Items */}
          <Card className="p-5 space-y-3">
            <h2 className="font-display text-base font-semibold text-ink">Line items</h2>
            {items.length === 0 ? (
              <p className="text-xs text-slatey italic">No items yet. Add one below.</p>
            ) : (
              <ul className="rounded-md border border-line divide-y divide-line">
                {items.map((it, i) => (
                  <li key={i} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1">
                      <p className="text-sm text-ink">{it.productName}</p>
                      <p className="text-xs text-slatey">
                        {formatINR(it.price)} × {it.quantity} ={' '}
                        <span className="font-medium text-ink">
                          {formatINR(it.price * it.quantity)}
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      className="size-7 rounded text-slatey hover:text-danger hover:bg-danger/10 inline-flex items-center justify-center"
                      title="Remove"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-[1fr_5rem_auto] gap-2 items-end">
              <FormField label="Product">
                <Select
                  value={addProductId}
                  onChange={(e) => setAddProductId(e.target.value)}
                  onFocus={ensureProductListLoaded}
                >
                  <option value="">— Pick a product —</option>
                  {productList.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                      disabled={p.totalStock === 0}
                    >
                      {p.name} — {formatINR(p.price)}
                      {' '}
                      {p.totalStock === 0
                        ? '(out of stock)'
                        : `(${p.totalStock} in stock)`}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Qty">
                <Input
                  type="number"
                  min={1}
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                />
              </FormField>
              <Button
                type="button"
                onClick={addItem}
                disabled={!addProductId}
                size="sm"
              >
                <Plus className="size-3.5" /> Add
              </Button>
            </div>
          </Card>

          {/* Address */}
          <Card className="p-5 space-y-3">
            <h2 className="font-display text-base font-semibold text-ink">Shipping address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Full name" required>
                <Input
                  value={addrName}
                  onChange={(e) => setAddrName(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Phone" required>
                <Input
                  value={addrPhone}
                  onChange={(e) => setAddrPhone(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Address line 1" required className="sm:col-span-2">
                <Input
                  value={addrLine1}
                  onChange={(e) => setAddrLine1(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Address line 2" className="sm:col-span-2">
                <Input
                  value={addrLine2}
                  onChange={(e) => setAddrLine2(e.target.value)}
                />
              </FormField>
              <FormField label="City" required>
                <Input
                  value={addrCity}
                  onChange={(e) => setAddrCity(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="State" required>
                <Input
                  value={addrState}
                  onChange={(e) => setAddrState(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Pincode" required>
                <Input
                  value={addrPincode}
                  onChange={(e) => setAddrPincode(e.target.value)}
                  maxLength={6}
                  required
                />
              </FormField>
            </div>
          </Card>

          <FormField
            label="Admin note (optional)"
            hint="Internal only — not shown to the customer."
          >
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="e.g. walk-in pickup, free sample for review"
            />
          </FormField>
        </div>

        {/* Right column: status + sticky totals */}
        <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Order meta</h2>
            <FormField label="Initial status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as AdminCreateStatus)}>
                <option value="created">Created (awaiting payment)</option>
                <option value="paid">Paid (cash / card collected)</option>
                <option value="delivered">Delivered (already shipped)</option>
              </Select>
            </FormField>
            <p className="text-xs text-slatey">
              Paid and delivered orders allocate stock immediately. Created
              orders defer allocation until payment.
            </p>
          </Card>

          <Card className="p-5 space-y-2">
            <h2 className="font-display text-base font-semibold text-ink">Totals</h2>
            <div className="text-sm divide-y divide-line">
              <Row label="Subtotal" value={formatINR(subtotalPaise)} />
              <Row label="GST (5%)" value={formatINR(gstPaise)} />
              <Row
                label="Shipping"
                value={shippingPaise === 0 ? 'Free' : formatINR(shippingPaise)}
              />
              <div className="py-2 flex items-center justify-between">
                <span className="font-semibold text-ink">Total</span>
                <span className="font-display text-base font-semibold text-ink">
                  {formatINR(totalPaise)}
                </span>
              </div>
            </div>
          </Card>
        </aside>
      </div>

      {/* Sticky bottom submit */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-white/90 backdrop-blur px-6 py-3 flex items-center justify-end gap-2">
        <span className="text-xs text-slatey mr-auto">
          {items.length} item{items.length === 1 ? '' : 's'}
        </span>
        <Button variant="outline" onClick={() => navigate('/orders')}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={submitting}>
          Create order · {formatINR(totalPaise)}
        </Button>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-2 flex items-center justify-between">
      <span className="text-slatey">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  )
}
